import { REFRESH_ACCESS_TOKEN_EXPIRATION_MINUTES } from 'modules/utils/ConfigUtils';
import { createSelector } from 'reselect';
import AuthServices from 'services/AuthServices';
import { currentAccountConsumerSelector } from 'store/account/selectors';
import { RootState } from 'store/types';
import { oc } from 'ts-optchain';
import { TpsSource, TpsStatus } from './tpsHelpers';

export const authStateSelector = (state: RootState) => state.authentication;

export const authenticationErrorSelector = createSelector(authStateSelector, auth => auth.error);

export const dfdAuthenticationSelector = createSelector(authStateSelector, auth => auth.dfdAuth);

export const dfdAccessTokenSelector = createSelector(dfdAuthenticationSelector, auth =>
  auth ? auth.accessToken : null
);

export const dfdRefreshTokenSelector = createSelector(dfdAuthenticationSelector, auth =>
  auth ? auth.refreshToken : null
);

export const isDfdAuthenticatingSelector = createSelector(
  authStateSelector,
  auth => auth.isAuthenticating
);

export const protectedRouteModalIsOpenSelector = createSelector(
  authStateSelector,
  auth => auth.protectedRouteModalIsOpen
);

export const hideGuestBenefitsModalSelector = createSelector(
  authStateSelector,
  auth => auth.hideGuestBenefitsModal
);

export const isDfdAuthenticatedSelector = (state: RootState): boolean =>
  AuthServices.isAuthenticated(dfdAuthenticationSelector(state));

export const shallRefreshDfdToken = (state: RootState): boolean =>
  AuthServices.shallRefreshToken(
    dfdAuthenticationSelector(state),
    REFRESH_ACCESS_TOKEN_EXPIRATION_MINUTES
  );

export const fhirCurrentUserPatientId = createSelector(
  currentAccountConsumerSelector,
  currentConsumer => {
    return currentConsumer.cernerPersonId;
  }
);

export const tpsAuthStatusSelector = (state: RootState): TpsStatus[] =>
  oc(state).authentication.tpsAuthStatus([]);

export const createTpsStatusSelector = (source: TpsSource) =>
  createSelector(tpsAuthStatusSelector, authStatus => {
    const sourceObject = authStatus.find(v => v.id === source);

    return sourceObject?.status === 'Authenticated';
  });

export const tpsFhirStatusSelector = createTpsStatusSelector(TpsSource.FHIR);
export const visitPayStatusSelector = createTpsStatusSelector(TpsSource.VISIT_PAY);

export const createTpsTokenSelector = (source: TpsSource) =>
  createSelector(tpsAuthStatusSelector, authStatus => {
    const sourceObject = authStatus.find(v => v.id === source);

    if (sourceObject) {
      return {
        accessToken: sourceObject.accessToken,
        expirationDate: sourceObject.expirationDate
      };
    }

    return null;
  });

export const tpsFhirTokenSelector = createTpsTokenSelector(TpsSource.FHIR);
export const tpsVisitPayTokenSelector = createTpsTokenSelector(TpsSource.VISIT_PAY);

// Auth Lite
export const authLiteSelector = (state: RootState) => state.authLite;
export const authLiteIsFetchingSelector = createSelector(
  authLiteSelector,
  authLite => authLite.isFetching
);
export const authLiteErrorSelector = createSelector(authLiteSelector, authLite => authLite.error);
export const authLiteDataSelector = createSelector(authLiteSelector, authLite => authLite.data);
